import apiClient from './apiClient';
import { Citation } from './rag';
import { EvaluationResponse } from './evaluations';

const apiBaseURL = '/api/conversations';

export interface MessageAttachment {
  id: string;
  filename: string;
  status: string;
}

/** Set by the backend when an assistant reply is a downloadable document. */
export interface DocumentExport {
  format: 'pdf' | 'docx' | 'md';
  title?: string;
  /** rule | tool | refusal_safety_net (export of a previous answer) or generated. */
  source?: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  content: string;
  is_user: boolean;
  metadata?: {
    sources: Citation[];
    persist_status?: string;
    debug_trace?: Record<string, unknown>;
    /** Absent on replies older than the export rework; null when not a document. */
    document_export?: DocumentExport | null;
    [key: string]: unknown;
  };
  timestamp: string;
  evaluation?: EvaluationResponse | null;
  /** Files the user attached when sending this message (GL-2.1). */
  attachments?: MessageAttachment[];
}

export interface Conversation {
  id: string;
  user_id: string;
  chatbot_id: string | null;
  organization_id: string | null;
  title: string;
  created_at: string;
  messages?: Message[];
}

export interface ConversationCreate {
  chatbot_id?: string;
  organization_id?: string;
  title?: string;
}

export interface ConversationUpdate {
  title?: string;
}

export type GeneratedFileFormat = 'md' | 'docx' | 'pdf' | 'pptx';

/**
 * Generation strategy for pptx (GL smart deck):
 * - `autonomous` → branded smart deck built from the organisation charter
 * - `template`   → template-constrained deck (requires a pptx `template_id`)
 * Ignored server-side for md/docx/pdf.
 */
export type GeneratedFileMode = 'autonomous' | 'template';

export interface GenerateConversationFileOptions {
  /** Optional when a template_id is provided — defaults to the template's output_format */
  format?: GeneratedFileFormat;
  template_id?: string;
  instructions?: string;
  title?: string;
  /** pptx-only: choose the autonomous branded deck vs a template-constrained deck. */
  mode?: GeneratedFileMode;
}

export interface ConversationUpload {
  id: string;
  conversation_id: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number;
  status: 'pending' | 'ready' | 'failed';
  error_message: string | null;
  created_at: string;
}

export interface MessageSend {
  content: string;
  context?: {
    domain_id?: string;
    use_case_id?: string;
    cadrage_id?: string;
    theme_codes?: string[];
    folder_paths?: string[];
  };
  assistant?: {
    content: string;
    citations?: Citation[];
  };
}

export interface MessageResponse {
  persist_status?: string;
  user_message: Message;
  assistant_message: Message;
  conversation: Conversation;
}

export interface StarterQuestionsResponse {
  questions: string[];
}

const STARTER_QUESTIONS_CACHE_TTL_MS = 60_000;
const starterQuestionsCache = new Map<string, { questions: string[]; expiresAt: number }>();
const starterQuestionsInFlight = new Map<string, Promise<string[]>>();

const buildStarterQuestionsKey = (options?: { chatbotId?: string; limit?: number }): string => {
  const chatbotPart = options?.chatbotId || 'platform';
  const limitPart = String(options?.limit ?? 4);
  return `${chatbotPart}::${limitPart}`;
};

type RawCitation = Partial<Citation> & Record<string, unknown>;
type RawMessage = Message & {
  metadata?: {
    sources?: RawCitation[] | null;
    debug_trace?: Record<string, unknown>;
    [key: string]: unknown;
  } | null;
};

const normalizeCitation = (citation: RawCitation, idx: number): Citation => ({
  index: typeof citation.index === 'number' ? citation.index : idx + 1,
  document_name:
    (typeof citation.document_name === 'string' && citation.document_name) ||
    (typeof citation.source_name === 'string' && citation.source_name) ||
    (typeof citation.filename === 'string' && citation.filename) ||
    (typeof citation.title === 'string' && citation.title) ||
    `Passage ${idx + 1}`,
  document_id:
    (typeof citation.document_id === 'string' && citation.document_id) ||
    (typeof citation.source_id === 'string' && citation.source_id) ||
    (typeof citation.id === 'string' && citation.id) ||
    '',
  text:
    (typeof citation.text === 'string' && citation.text) ||
    (typeof citation.content === 'string' && citation.content) ||
    (typeof citation.snippet === 'string' && citation.snippet) ||
    '',
  chunk_index: typeof citation.chunk_index === 'number' ? citation.chunk_index : 0,
});

const extractSources = (message: RawMessage): Citation[] => {
  const metadataSources = message.metadata?.sources;
  if (Array.isArray(metadataSources)) {
    return metadataSources.map((citation, idx) => normalizeCitation(citation as RawCitation, idx));
  }

  return [];
};

const normalizeMessage = (message: RawMessage): Message => {
  const normalizedSources = extractSources(message);

  return {
    ...message,
    metadata: {
      ...(message.metadata || {}),
      sources: normalizedSources,
    },
  };
};

const normalizeConversation = (conversation: Conversation): Conversation => ({
  ...conversation,
  messages: (conversation.messages as RawMessage[] | undefined)?.map(normalizeMessage) || [],
});

export const conversationsService = {
  /**
   * List user's conversations (optionally filtered by chatbot)
   */
  list: async (chatbotId?: string): Promise<Conversation[]> => {
    const params = chatbotId ? { chatbot_id: chatbotId } : {};
    const response = await apiClient.get(`${apiBaseURL}/`, { params });
    return (response.data as Conversation[]).map(normalizeConversation);
  },

  /**
   * Get specific conversation with all messages
   */
  get: async (id: string): Promise<Conversation> => {
    const response = await apiClient.get(`${apiBaseURL}/${id}`);
    return normalizeConversation(response.data as Conversation);
  },

  /**
   * Create new conversation
   */
  create: async (data: ConversationCreate): Promise<Conversation> => {
    const response = await apiClient.post(`${apiBaseURL}/`, data);
    return response.data;
  },

  /**
   * Generate AI starter questions for a new conversation.
   */
  getStarterQuestions: async (
    options?: { chatbotId?: string; limit?: number }
  ): Promise<string[]> => {
    const key = buildStarterQuestionsKey(options);
    const now = Date.now();
    const cached = starterQuestionsCache.get(key);
    if (cached && cached.expiresAt > now) {
      return cached.questions;
    }

    const pending = starterQuestionsInFlight.get(key);
    if (pending) {
      return pending;
    }

    const request = (async () => {
      const response = await apiClient.get(`${apiBaseURL}/starter-questions`, {
        params: {
          chatbot_id: options?.chatbotId,
          limit: options?.limit ?? 4,
        },
      });
      const payload = response.data as StarterQuestionsResponse;
      const questions = Array.isArray(payload.questions) ? payload.questions : [];
      starterQuestionsCache.set(key, {
        questions,
        expiresAt: Date.now() + STARTER_QUESTIONS_CACHE_TTL_MS,
      });
      return questions;
    })().finally(() => {
      starterQuestionsInFlight.delete(key);
    });

    starterQuestionsInFlight.set(key, request);
    return request;
  },

  /**
   * Send message in conversation
   */
  sendMessage: async (conversationId: string, data: MessageSend): Promise<MessageResponse> => {
    const response = await apiClient.post(`${apiBaseURL}/${conversationId}/messages`, data);
    const payload = response.data as {
      persist_status?: string;
      user_message: RawMessage;
      assistant_message: RawMessage;
      conversation: Conversation;
    };

    return {
      persist_status: payload.persist_status,
      user_message: normalizeMessage(payload.user_message),
      assistant_message: normalizeMessage(payload.assistant_message),
      conversation: normalizeConversation(payload.conversation),
    };
  },

  /**
   * Attach a file to a conversation (text extraction is synchronous server-side:
   * the returned status is already 'ready' or 'failed')
   */
  uploadConversationFile: async (
    conversationId: string,
    file: File
  ): Promise<ConversationUpload> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${apiBaseURL}/${conversationId}/uploads`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as ConversationUpload;
  },

  /**
   * List files attached to a conversation
   */
  listConversationUploads: async (conversationId: string): Promise<ConversationUpload[]> => {
    const response = await apiClient.get(`${apiBaseURL}/${conversationId}/uploads`);
    return response.data as ConversationUpload[];
  },

  /**
   * Remove a file attached to a conversation
   */
  deleteConversationUpload: async (conversationId: string, uploadId: string): Promise<void> => {
    await apiClient.delete(`${apiBaseURL}/${conversationId}/uploads/${uploadId}`);
  },

  /**
   * Generate a downloadable document from the conversation (md/docx/pdf/pptx).
   * When a template_id is provided, format falls back to the template's output_format.
   * For pptx, `mode` selects the autonomous branded deck vs a template-constrained deck.
   * Returns the file blob and its server-provided filename.
   */
  generateConversationFile: async (
    conversationId: string,
    options: GenerateConversationFileOptions
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await apiClient.post(
      `${apiBaseURL}/${conversationId}/generate-file`,
      options,
      { responseType: 'blob' }
    );
    const disposition: string = response.headers['content-disposition'] ?? '';
    const match = disposition.match(/filename\*=UTF-8''([^;]+)/);
    const filename = match
      ? decodeURIComponent(match[1])
      : `document${options.format ? `.${options.format}` : ''}`;
    return { blob: response.data as Blob, filename };
  },

  /**
   * Delete conversation
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${apiBaseURL}/${id}`);
  },
};
